import { Checkbox, Form, Radio } from 'antd'
import { Rule } from 'rc-field-form/es/interface'
import { ReactNode, useMemo } from 'react'
import { ExportSetting } from '@/components/kol/export/kol-export-modal'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PlatformShortcode } from '@/utils/convert-platform'
import { SupportedPlatformList } from '@/utils/export-pptx/constants'

type MakeFormItem = (
  section: keyof ExportSetting,
  required: boolean,
  sectionStyle: 'radio' | 'checkbox',
  initialValue?: unknown,
) => ReactNode
type UseMakeExportFormItem = (
  kolId?: string,
  kolDcIdHashed?: string,
) => MakeFormItem

const getExportI18nId = (key: string): I18nId => {
  const dict: Record<string, I18nId> = {
    platforms: 'project:collection_export_platform',
    period: 'project:collection_export_period',
    three_months: 'project:collection_export_period_three_months',
    six_months: 'project:collection_export_period_six_months',
    [PlatformShortcode.Facebook]: 'project:collection_export_platform_facebook',
    [PlatformShortcode.Instagram]:
      'project:collection_export_platform_instagram',
    [PlatformShortcode.YouTube]: 'project:collection_export_platform_youtube',
    [PlatformShortcode.TikTok]: 'project:collection_export_platform_tiktok',
    [PlatformShortcode.Twitter]: 'project:collection_export_platform_twitter',
    [PlatformShortcode.Threads]: 'project:collection_export_platform_threads',
  }

  return dict[key] || 'general:empty'
}

export const platformOrder = [
  PlatformShortcode.Instagram,
  PlatformShortcode.Facebook,
  PlatformShortcode.YouTube,
  PlatformShortcode.TikTok,
  PlatformShortcode.Twitter,
  PlatformShortcode.Threads,
]

const useMakeFormItem: UseMakeExportFormItem = (kolId, kolDcIdHashed) => {
  const { formatMessage } = useIntl()
  const { kolInfo } = useKolInfo(kolId, kolDcIdHashed)
  const period = ['three_months', 'six_months']
  const platforms = useMemo<string[]>(() => {
    if (!kolInfo) {
      return []
    }

    return kolInfo.kpi
      .map((kpi) => kpi.platform as PlatformShortcode)
      .filter((platform) => SupportedPlatformList.includes(platform))
      .sort((a, b) => {
        return platformOrder.indexOf(a) - platformOrder.indexOf(b)
      })
  }, [kolInfo])

  const group = {
    period,
    platforms,
  }

  const makeFormItem: MakeFormItem = (
    section,
    required,
    sectionStyle,
    initialValue,
  ) => {
    const label = formatMessage({
      id: getExportI18nId(section),
    })
    const rules: Rule[] = [
      {
        required,
        message: formatMessage(
          { id: 'error:please_select' },
          {
            selectField: formatMessage({
              id: getExportI18nId(section),
            }),
          },
        ),
      },
    ]

    switch (sectionStyle) {
      case 'radio':
        return (
          <Form.Item
            initialValue={initialValue}
            label={label}
            name={section}
            required={required}
            rules={rules}
          >
            <Radio.Group>
              {group[section].map((key) => {
                return (
                  <Radio key={key} value={key}>
                    {formatMessage({
                      id: getExportI18nId(key),
                    })}
                  </Radio>
                )
              })}
            </Radio.Group>
          </Form.Item>
        )
      case 'checkbox':
        return (
          <Form.Item
            initialValue={initialValue}
            label={label}
            name={section}
            required={required}
            rules={rules}
          >
            <Checkbox.Group>
              {group[section].map((key) => {
                return (
                  <Checkbox key={key} value={key}>
                    {formatMessage({
                      id: getExportI18nId(key),
                    })}
                  </Checkbox>
                )
              })}
            </Checkbox.Group>
          </Form.Item>
        )
    }
  }

  return makeFormItem
}

export default useMakeFormItem
